import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Badge, Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { Context } from "../App";
import AdminLayout from "../components/AdminLayout";
import * as Constants from "../api/constants";
import "../adminConsoleTheme.css";

const ROLE_HEADER = "X-Admin-Role";
/** Roles allowed to open customer leads (must match server {@code PromoCodeAdminController}). */
const ROLES_LEADS = Object.freeze({
  SUPER_ADMIN: "SUPER_ADMIN",
  ADMIN: "ADMIN",
});
const ADMIN_ROLES = new Set([ROLES_LEADS.SUPER_ADMIN, ROLES_LEADS.ADMIN]);

/**
 * The set of follow-up states the internal team can move a lead through.
 * Mirrors the values the backend persists in `promo_code_lead.lead_status`.
 */
const STATUSES = [
  { value: "ALL", label: "All" },
  { value: "NEW", label: "New" },
  { value: "CONTACTED", label: "Contacted" },
  { value: "BOOKED", label: "Booked" },
  { value: "NOT_INTERESTED", label: "Not interested" },
  { value: "DISCARDED", label: "Discarded" },
];

const STATUS_VARIANTS = {
  NEW: "warning",
  CONTACTED: "info",
  BOOKED: "success",
  NOT_INTERESTED: "secondary",
  DISCARDED: "dark",
};

const SOURCE_LABEL = {
  WELCOME_POPUP: "Welcome popup",
  CALLBACK_REQUEST: "Callback request",
  BOOKING: "Booking confirmed",
};

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function maskEmail(value) {
  if (!value) return "";
  return String(value).trim();
}

function buildTelLink(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  const prefix = digits.length === 10 ? "+91" : "+";
  return `tel:${prefix}${digits}`;
}

function buildWhatsAppLink(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  const intl = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${intl}`;
}

function LeadCustomerCell({ data }) {
  if (!data) return null;
  return (
    <div className="admin-console-grid-cell-inner">
      <div className="admin-console-grid-cell-name">{data.customerName || "—"}</div>
      {data.notes ? (
        <div className="admin-console-grid-cell-subline" style={{ whiteSpace: "pre-wrap", maxWidth: 220 }}>
          {data.notes}
        </div>
      ) : null}
    </div>
  );
}

function LeadContactCell({ data }) {
  if (!data) return null;
  const tel = buildTelLink(data.phoneNumber);
  const wa = buildWhatsAppLink(data.phoneNumber);
  return (
    <div className="admin-console-grid-cell-inner small">
      <div>
        {tel ? (
          <a href={tel} className="admin-console-grid-cell-phone" onClick={(e) => e.stopPropagation()}>
            {data.phoneNumber}
          </a>
        ) : (
          <span className="admin-console-grid-cell-phone">{data.phoneNumber || "—"}</span>
        )}
      </div>
      {data.email ? (
        <div>
          <a
            href={`mailto:${data.email}`}
            className="admin-console-grid-cell-contact-sub"
            onClick={(e) => e.stopPropagation()}
          >
            {maskEmail(data.email)}
          </a>
        </div>
      ) : null}
      {wa ? (
        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          className="admin-console-grid-cell-contact-sub"
          onClick={(e) => e.stopPropagation()}
        >
          WhatsApp
        </a>
      ) : null}
    </div>
  );
}

function LeadPromoCell({ data }) {
  if (!data?.promoCode) return <span className="admin-console-grid-cell-subline">—</span>;
  return (
    <div className="admin-console-grid-cell-inner small">
      <code className="admin-console-grid-cell-promo-code text-uppercase">{data.promoCode}</code>
      <div className="admin-console-grid-cell-subline">
        Rs. {data.promoDiscountAmount || 0} off
        {data.promoActive === false ? (
          <Badge bg="secondary" className="ms-1">
            inactive
          </Badge>
        ) : null}
        {data.alreadyRedeemed ? (
          <Badge bg="success" className="ms-1">
            redeemed
          </Badge>
        ) : null}
      </div>
    </div>
  );
}

function LeadStatusCell({ data }) {
  if (!data) return null;
  const status = String(data.leadStatus || "NEW").toUpperCase();
  return <Badge bg={STATUS_VARIANTS[status] || "secondary"}>{status}</Badge>;
}

function LeadActionsCell({ data, context }) {
  if (!data?.id) return null;
  const { onQuickStatus, onEditRow } = context || {};
  const status = String(data.leadStatus || "NEW").toUpperCase();

  const stopClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <div className="admin-console-grid-actions">
      {status === "NEW" ? (
        <button
          type="button"
          className="btn btn-sm admin-console-grid-btn-gold"
          onClick={(e) => {
            stopClick(e);
            onQuickStatus?.(data, "CONTACTED");
          }}
        >
          Mark contacted
        </button>
      ) : null}
      {status !== "BOOKED" ? (
        <button
          type="button"
          className="btn btn-sm admin-console-grid-btn-gold"
          onClick={(e) => {
            stopClick(e);
            onQuickStatus?.(data, "BOOKED");
          }}
        >
          Booked
        </button>
      ) : null}
      <button
        type="button"
        className="btn btn-sm admin-console-grid-btn-gold"
        onClick={(e) => {
          stopClick(e);
          onEditRow?.(data);
        }}
      >
        Edit
      </button>
    </div>
  );
}

/**
 * Super-admin only: lead/callback queue captured by the welcome promo popup.
 */
export default function AdminCustomerLeads() {
  const navigate = useNavigate();
  const { context } = useContext(Context);
  const gridRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [banner, setBanner] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draftStatus, setDraftStatus] = useState("NEW");
  const [draftNotes, setDraftNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const role = String(context?.adminRole || "").toUpperCase();

  useEffect(() => {
    if (!context.isAdminLoggedIn) {
      navigate("/admin", { replace: true });
      return;
    }
    if (!ADMIN_ROLES.has(role)) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [context.isAdminLoggedIn, role, navigate]);

  const showBanner = useCallback((variant, message) => {
    setBanner({ variant, message });
    window.setTimeout(() => setBanner(null), 5000);
  }, []);

  const headers = useMemo(
    () => ({
      [ROLE_HEADER]: role,
      "X-Admin-User": context?.adminUserName || "",
    }),
    [role, context?.adminUserName]
  );

  const loadList = useCallback(() => {
    setLoading(true);
    const params = statusFilter && statusFilter !== "ALL" ? { status: statusFilter } : {};
    axios
      .get(Constants.ADMIN_CUSTOMER_LEADS, { headers, params })
      .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        const msg = err?.response?.data?.message || "Could not load customer leads.";
        showBanner("danger", msg);
      })
      .finally(() => setLoading(false));
  }, [headers, showBanner, statusFilter]);

  useEffect(() => {
    if (ADMIN_ROLES.has(role)) loadList();
  }, [role, loadList]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const blob = `${r.customerName || ""} ${r.phoneNumber || ""} ${r.email || ""} ${r.promoCode || ""}`
        .toLowerCase();
      return blob.includes(q);
    });
  }, [rows, search]);

  const counts = useMemo(() => {
    const out = { total: rows.length, NEW: 0, CONTACTED: 0, BOOKED: 0 };
    rows.forEach((r) => {
      const k = String(r.leadStatus || "NEW").toUpperCase();
      if (out[k] !== undefined) out[k] += 1;
    });
    return out;
  }, [rows]);

  const openEdit = useCallback((row) => {
    setEditing(row);
    setDraftStatus(row.leadStatus || "NEW");
    setDraftNotes(row.notes || "");
  }, []);

  const closeEdit = () => {
    setEditing(null);
    setSaving(false);
  };

  const onSave = async () => {
    if (!editing?.id) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(
        Constants.adminCustomerLeadUrl(editing.id),
        { leadStatus: draftStatus, notes: draftNotes },
        { headers }
      );
      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      showBanner("success", `Updated lead for ${data.customerName}.`);
      closeEdit();
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not save the update.";
      showBanner("danger", msg);
    } finally {
      setSaving(false);
    }
  };

  const onQuickStatus = useCallback(
    async (row, nextStatus) => {
      if (!row?.id || row.leadStatus === nextStatus) return;
      try {
        const { data } = await axios.patch(
          Constants.adminCustomerLeadUrl(row.id),
          { leadStatus: nextStatus, notes: row.notes || "" },
          { headers }
        );
        setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
        showBanner("success", `Marked ${data.customerName} as ${nextStatus}.`);
      } catch (err) {
        const msg = err?.response?.data?.message || "Could not update.";
        showBanner("danger", msg);
      }
    },
    [headers, showBanner]
  );

  const gridContext = useMemo(
    () => ({
      onQuickStatus,
      onEditRow: openEdit,
    }),
    [onQuickStatus, openEdit]
  );

  const leadColumnDefs = useMemo(
    () => [
      {
        headerName: "Customer",
        width: 200,
        wrapText: true,
        autoHeight: true,
        cellRenderer: LeadCustomerCell,
      },
      {
        headerName: "Contact",
        width: 180,
        wrapText: true,
        autoHeight: true,
        cellRenderer: LeadContactCell,
      },
      {
        headerName: "Promo",
        width: 150,
        cellRenderer: LeadPromoCell,
      },
      {
        headerName: "Source",
        field: "source",
        width: 140,
        valueFormatter: (p) => SOURCE_LABEL[p.value] || p.value || "—",
      },
      {
        headerName: "Captured",
        field: "createdDate",
        width: 160,
        valueFormatter: (p) => formatDateTime(p.value),
      },
      {
        headerName: "Status",
        width: 120,
        cellRenderer: LeadStatusCell,
      },
      {
        headerName: "Last contacted",
        width: 170,
        wrapText: true,
        autoHeight: true,
        valueGetter: (p) => {
          const row = p.data;
          if (!row) return "—";
          const when = formatDateTime(row.lastContactedDate);
          return row.updatedBy ? `${when}\nby ${row.updatedBy}` : when;
        },
      },
      {
        headerName: "Actions",
        pinned: "right",
        width: 130,
        sortable: false,
        filter: false,
        cellClass: "admin-console-grid-actions-cell",
        cellRenderer: LeadActionsCell,
        suppressNavigable: true,
      },
    ],
    []
  );

  const noRowsOverlay =
    search.trim() !== ""
      ? "No leads match your search."
      : "No leads to show yet. They appear here as customers submit the welcome popup or complete a booking.";

  if (!ADMIN_ROLES.has(role)) return null;

  return (
    <AdminLayout title="Customer leads">
      <div className="admin-console-card p-3 rounded border border-secondary">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
          <div>
            <h2 className="h6 text-info mb-1">Customer leads</h2>
            <p className="small text-secondary mb-0" style={{ maxWidth: 640 }}>
              Captured from the welcome popup (with or without an active promo code) and auto-appended
              whenever a booking is saved. Track callbacks: New → Contacted → Booked / Not interested.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Badge bg="secondary">Total: {counts.total}</Badge>
            <Badge bg="warning" text="dark">
              New: {counts.NEW}
            </Badge>
            <Badge bg="info">Contacted: {counts.CONTACTED}</Badge>
            <Badge bg="success">Booked: {counts.BOOKED}</Badge>
            <Button variant="outline-light" size="sm" onClick={loadList} disabled={loading}>
              Refresh
            </Button>
            {loading ? <Spinner animation="border" size="sm" className="text-light" /> : null}
          </div>
        </div>

        {banner ? (
          <Alert variant={banner.variant} dismissible onClose={() => setBanner(null)} className="mb-3 py-2">
            {banner.message}
          </Alert>
        ) : null}

        <Form className="admin-console-filters d-flex flex-wrap gap-2 mb-3">
          <InputGroup size="sm" style={{ maxWidth: 260 }}>
            <InputGroup.Text>
              <i className="bi bi-search" />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search name / phone / email / code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search ? (
              <Button variant="outline-secondary" onClick={() => setSearch("")}>
                Clear
              </Button>
            ) : null}
          </InputGroup>

          <Form.Select
            size="sm"
            style={{ maxWidth: 200 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Form.Select>
        </Form>

        <div className="admin-console-card-title px-1 mb-2">
          Lead list — use actions to update status or edit notes
        </div>
        <div className="admin-console-grid-wrap admin-console-grid-wrap--light ag-theme-quartz" style={{ height: 520 }}>
          <AgGridReact
            ref={gridRef}
            rowData={visibleRows}
            columnDefs={leadColumnDefs}
            context={gridContext}
            rowSelection="single"
            rowHeight={72}
            suppressCellFocus
            overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center small text-secondary">${noRowsOverlay}</span>`}
            loading={loading}
            defaultColDef={{
              sortable: true,
              filter: false,
              resizable: true,
              suppressMovable: true,
            }}
          />
        </div>
      </div>

      <Modal show={Boolean(editing)} onHide={closeEdit} centered className="admin-console-modal">
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{editing ? `Update ${editing.customerName}` : "Update lead"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing ? (
            <>
              <div className="mb-3">
                <div className="text-secondary small">Phone</div>
                <div>{editing.phoneNumber}</div>
              </div>
              {editing.email ? (
                <div className="mb-3">
                  <div className="text-secondary small">Email</div>
                  <div>{editing.email}</div>
                </div>
              ) : null}
              {editing.promoCode ? (
                <div className="mb-3">
                  <div className="text-secondary small">Promo</div>
                  <div>
                    <code>{editing.promoCode}</code> · Rs. {editing.promoDiscountAmount || 0} off
                  </div>
                </div>
              ) : null}
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select value={draftStatus} onChange={(e) => setDraftStatus(e.target.value)}>
                  {STATUSES.filter((s) => s.value !== "ALL").map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  placeholder="Call summary, preferred slot, follow-up reminders…"
                  maxLength={2000}
                />
              </Form.Group>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </AdminLayout>
  );
}
